import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
} from "@angular/core";
import { AuthService } from "src/app/core/authentication/auth.service";
import { serverUrl } from "src/app/shared/constants";
import { sessionProfile } from "../../interfaces/sessionStorageModel";
import { MessageService } from "../../services/message/message.service";
import { SessionStorageService } from "../../services/session-storage/session-storage.service";
import { Observable, timer, Subscription } from "rxjs";
import { switchMap, retry } from "rxjs/operators";
import { CommonHttpService } from "../../services/common-http/common-http.service";
const Resumable = (window as any).Resumable;
@Component({
    selector: "app-video-chunks-upload",
    templateUrl: "./video-chunks-upload.component.html",
    styleUrls: ["./video-chunks-upload.component.scss"],
    standalone: false
})
export class VideoChunksUploadComponent implements OnDestroy {
  profile: sessionProfile;
  resumableObject: any;
  Resumable = Resumable;

  extensionAllowedArray = ["mp4", "webm", "mov"];
  chunkSize = 0.5 * 1024 * 1024;
  permanentErrors = [401, 400, 500, 501];
  maxChunkRetries = 50;
  chunkRetryInterval = 5000;
  @Input() chunksClass: string = "";
  @Input() apiUrl: string;
  @Output() progressPercentage: EventEmitter<any> = new EventEmitter();
  @Output() fileStatusUpdate: EventEmitter<any> = new EventEmitter();
  private checkFileStatus$: Observable<any>;
  private checkFileStatusSubscription$: Subscription;
  poolingTime: number;
  constructor(
    private sessionStorageService: SessionStorageService,
    private authService: AuthService,
    private messageService: MessageService,
    private commonHttpService: CommonHttpService
  ) {
    this.profile = this.sessionStorageService.getLoggedInProfileDetail();
  }
  // The folder every chunk of the current upload is written to. It must stay stable
  // for the whole upload: the server only merges a video once the chunk count in the
  // folder matches resumableTotalChunks, so a folder that changes mid-upload leaves
  // every folder short and the upload is rejected as "chunk missing" for ever.
  private uploadFolderPath: string;

  private newUploadFolderPath() {
    this.uploadFolderPath =
      "videofile_" + this.profile.id + "_" + new Date().getTime();
    return this.uploadFolderPath;
  }

  getUploadQuery() {
    const folderPath = this.uploadFolderPath || this.newUploadFolderPath();
    return {
      folderPath,
      createasset: true,
      convertvideo: true,
      mediatype: 2,
      access_token: this.authService.token(),
      isChallengeVideo: false,
    };
  }

  uploadLocalFile(event) {
    const selectedFile = event.target.files[0] as File;
    const fileExtension = selectedFile.name.substring(
      selectedFile.name.lastIndexOf(".") + 1
    );
    if (
      selectedFile &&
      this.extensionAllowedArray.includes(fileExtension.toLowerCase())
    ) {
      // A fresh folder per upload attempt; every chunk of this attempt reuses it.
      this.newUploadFolderPath();
      this.resumableObject = this.Resumable({
        testChunks: false,
        chunkSize: this.chunkSize, // 10 * 1024 * 1024,
        target: serverUrl + this.apiUrl,
        maxChunkRetries: this.maxChunkRetries,
        chunkRetryInterval: this.chunkRetryInterval,
        withCredentials: true,
        permanentErrors: this.permanentErrors,
        query: this.getUploadQuery(),
        headers: {
          "X-XSRF-TOKEN": this.getCookie("XSRF-TOKEN"),
        },
      });
      this.chunkUploadFileToServer(selectedFile, true);
    } else {
      const isAre = this.extensionAllowedArray.length > 1 ? "are" : "is";
      this.messageService.showErrorMessage(
        `This is not a valid extension. Supported extension(s)${isAre} ${this.extensionAllowedArray}.`
      );
    }
  }

  removeCookie(sKey, sPath, sDomain) {
    document.cookie =
      encodeURIComponent(sKey) +
      "=; expires=Thu, 01 Jan 1970 00:00:00 GMT" +
      (sDomain ? "; domain=" + sDomain : "") +
      (sPath ? "; path=" + sPath : "");
  }

  getCookie(cname) {
    const name = cname + "=";
    const decodedCookie = decodeURIComponent(document.cookie);
    const ca = decodedCookie.split(";");
    for (const eachCA of ca) {
      let c = eachCA;
      while (c.charAt(0) === " ") {
        c = c.substring(1);
      }
      if (c.indexOf(name) === 0) {
        return c.substring(name.length, c.length);
      }
    }
    return "";
  }

  chunkUploadFileToServer(videoFile, isUploadFile) {
    const assetSize = videoFile.size / (1024 * 1024);
    this.poolingTime = assetSize < 600 ? 6000 : assetSize * 10;
    if (assetSize < 500) {
      this.messageService.showInfoMessage(
        "The video may take 5 to 15 minutes to process for upload."
      );
    } else if (assetSize < 1000) {
      this.messageService.showInfoMessage(
        "The video may take 15 to 30 minutes to process for upload."
      );
    } else if (assetSize >= 1000 && assetSize <= 1536) {
      this.messageService.showInfoMessage(
        "The video may take 30 to 45 minutes to process for upload."
      );
    } else {
      this.messageService.showErrorMessage(
        `Your video file is too large. The recommended video size 1.5 GB or less. Please contact Support@vPlaybook.com if you need assistance`
      );
      return;
    }
    this.progressPercentage.emit({ percentage: 0, text: "Processing" });
    let currentProgTemp = 0;
    this.resumableObject.on("fileAdded", (file, event) => {
      this.progressPercentage.emit({ percentage: 0, text: "" });
    });

    this.resumableObject.on("fileSuccess", (file, message) => {
      this.progressPercentage.emit({ percentage: "Processing", text: "" });
      const msgJson = JSON.parse(message);
      if (+msgJson.Result.chunkStatus === 1) {
        this.getVideoToken(videoFile, msgJson);
      } else if (+msgJson.Result.chunkStatus === 2) {
        this.messageService.showErrorMessage(
          "Something went wrong please refresh the page and try again."
        );
      }
    });

    this.resumableObject.on("fileError", (file, message) => {
      this.messageService.showErrorMessage(
        "Something went wrong please refresh the page and try again."
      );
      this.fileStatusUpdate.emit({ status: false });
    });

    this.resumableObject.on("progress", () => {
      const currentRoundProg = Math.floor(
        this.resumableObject.progress() * 100
      );
      if (currentRoundProg > 0 && currentRoundProg > currentProgTemp) {
        currentProgTemp = currentRoundProg;
        this.progressPercentage.emit({ percentage: currentProgTemp, text: "" });
      }

      this.resumableObject.query = this.getUploadQuery();
    });

    this.resumableObject.addFile(videoFile);

    setTimeout(() => {
      this.resumableObject.upload();
    }, 100);
  }
  secondsToHms(d) {
    d = Number(d);
    const h = Math.floor(d / 3600);
    const m = Math.floor((d % 3600) / 60);
    const s = Math.floor((d % 3600) % 60);

    const hDisplay = h > 0 ? h + (h === 1 ? " hour, " : " hours, ") : "";
    const mDisplay = m > 0 ? m + (m === 1 ? " minute, " : " minutes, ") : "";
    const sDisplay = s > 0 ? s + (s === 1 ? " second" : " seconds") : "";
    return hDisplay + mDisplay + sDisplay;
  }

  getVideoToken(videoFile, videoFileData) {
    this.unSubscribeCheckFileStatus();
    this.checkFileStatus$ = timer(1, this.poolingTime).pipe(
      switchMap(() =>
        this.checkVideoFile(+videoFileData.Result.backgroundVideoId)
      ),
      retry(1)
    );
    this.checkFileStatusSubscription$ = this.checkFileStatus$.subscribe({
      next: (response: any) => {
        if (response.status === 3) {
          this.unSubscribeCheckFileStatus();
          this.setVideoAsset(response, videoFile, videoFileData);
        } else if (response.status === 2) {
          this.unSubscribeCheckFileStatus();
          this.fileStatusUpdate.emit({ status: false });
        }
      },
      // Without this the caller is never told the polling observable died.
      error: () => {
        this.unSubscribeCheckFileStatus();
        this.fileStatusUpdate.emit({ status: false });
      },
    });
  }
  unSubscribeCheckFileStatus() {
    if (this.checkFileStatusSubscription$) {
      this.checkFileStatusSubscription$.unsubscribe();
    }
  }
  checkVideoFile(videoId): Observable<any> {
    return this.commonHttpService.get({
      uri: `/api/Assets/GetVideoProcessStatus?id=${videoId}`,
    });
  }
  setVideoAsset(downloadResponse, videoFile, videoFileData) {
    this.fileStatusUpdate.emit({
      status: true,
      file: videoFile,
      assetData: videoFileData.Result.asset,
      url: downloadResponse,
    });
  }

  ngOnDestroy(): void {
    if (this.resumableObject && this.resumableObject.isUploading()) {
      this.resumableObject.cancel();
    }
    this.unSubscribeCheckFileStatus();
  }
}
